import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { supportedLanguages } from "../../../constants";
import { changeLanguage } from "../../../redux/slices/messages.slice";
import { getCurrentUserData } from "../../../redux/slices/current-user.slice";
import { getOrderStates } from "../../../redux/slices/static-data.slice";
import {
  changeState,
  getOrdersStatesSummary,
} from "../../../redux/slices/orders-slice";
import { MessageLoader } from "./message.loader";
import { MessageSections } from "../../../pages/components/message/message";
import Header from "../../../pages/header";
import AuthBackground from "../../../pages/auth/background";
import { CircularProgress, Snackbar } from "@mui/material";
import { LoadingSpinnerContainer } from "../../styled";
import { UserGroups } from "../../router";
import { setGlobalHeaderRef } from "../../../pages/shared/refs";
import { changeSidebarItems } from "../../../redux/slices/sidebar.slice";
import { HomePage } from "../../../pages/home";

export const LanguageRouteComponent = React.memo(() => {
  const [isReady, setIsReady] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const headerRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const token = localStorage.getItem("access_token");
  const { language } = useParams(); // language abbreviation
  const { languageId } = useAppSelector((state) => state.messagesReducer); // current language userId
  const { matchesPhone } = useAppSelector((state) => state.deviceReducer);
  const { orderStates } = useAppSelector((state) => state.staticDataReducer);

  const paramsLanguageIndex = supportedLanguages.findIndex(
    (abbr) => abbr === language,
  );
  const paramsLanguageId =
    paramsLanguageIndex !== -1 ? paramsLanguageIndex + 1 : undefined;
  const path = location.pathname;

  const userUploadsLoading = useAppSelector(
    (state) => state.userUploadsReducer,
  ).isLoading;
  const loginLoading = useAppSelector((state) => state.loginReducer).isLoading;
  const ordersLoading = useAppSelector(
    (state) => state.ordersReducer,
  ).isLoading;
  const registrationLoading = useAppSelector(
    (state) => state.registrationReducer,
  ).isLoading;
  const newOrderLoading = useAppSelector(
    (state) => state.newOrderReducer,
  ).isLoading;
  const recipientsLoading = useAppSelector(
    (state) => state.recipientDataReducer,
  ).isLoading;
  const userAddressesLoading = useAppSelector(
    (state) => state.userAddressesReducer,
  ).isLoading;
  const notificationsLoading = useAppSelector(
    (state) => state.userNotificationsReducer,
  ).isLoading;
  const CRMLoading = useAppSelector((state) => state.CRMReducer).isLoading;

  const isLoading =
    userUploadsLoading ||
    loginLoading ||
    ordersLoading ||
    registrationLoading ||
    newOrderLoading ||
    recipientsLoading ||
    userAddressesLoading ||
    notificationsLoading ||
    CRMLoading;

  const { currentUser } = useAppSelector((state) => state.currentUserReducer);
  const mainRecipientDocumentNumber =
    (currentUser &&
      currentUser.relatedRecipientId &&
      currentUser.relatedRecipientId.passportOrIdSerialNumber) ||
    "";

  const isInAuthPage =
    path.includes(`/${language}/login`) ||
    path.includes(`/${language}/register`) ||
    path.includes(`/${language}/change-password`) ||
    path.includes(`/${language}/account-activation`) ||
    path.includes(`/${language}/recover`);

  const [hasValidatedRecipient, setHasValidatedRecipient] = useState(false);

  useEffect(() => {
    setGlobalHeaderRef(headerRef);

    if (!paramsLanguageId) {
      // defaults to "arm" when incorrect
      dispatch(changeLanguage(1));
      navigate("/hy", { replace: true });
    } else if (paramsLanguageId !== languageId) {
      dispatch(changeLanguage(paramsLanguageId));
    }

    const modifiedPath = path.replace(
      `/${supportedLanguages[languageId]}/`,
      `/${language}/`,
    );

    if (modifiedPath !== path) {
      navigate(path + location.search + location.hash);
      setIsReady(true);
    } else {
      if (!token) {
        setHasValidatedRecipient(false);

        if (!isInAuthPage) {
          navigate(`../${language}/`, { replace: true });
        }

        setIsReady(true);
      } else {
        dispatch(getCurrentUserData());
        dispatch(getOrderStates());
      }
    }
  }, [paramsLanguageId, token, mainRecipientDocumentNumber]);

  const isInLanguagePage = useMemo(() => path === `/${language}` || path === `/${language}/`, [path, language]);

  // Helper to move one item after another
  function moveAfter(arr: {
    id: number;
    name: string;
    countable: boolean;
  }[], fromName: string, afterName: string): void {
    const fromIndex = arr.findIndex(item => item.name === fromName);
    const afterIndex = arr.findIndex(item => item.name === afterName);

    if (fromIndex > -1 && afterIndex > -1 && fromIndex !== afterIndex + 1) {
      const [item] = arr.splice(fromIndex, 1);
      const insertIndex = afterIndex < fromIndex ? afterIndex + 1 : afterIndex;
      arr.splice(insertIndex, 0, item);
    }
  }

  useEffect(() => {
    if (currentUser && orderStates.length) {
      setHasValidatedRecipient(
        !!currentUser.relatedRecipientId?.passportOrIdSerialNumber,
      );

      const isWarehouseWorker =
        currentUser.userGroup.userGroupId === UserGroups.WAREHOUSE_WORKER;
      const isLocalWarehouseWorker =
        currentUser.userGroup.userGroupId === UserGroups.LOCAL_WAREHOUSE_WORKER;
      const isAdmin = currentUser.userGroup.userGroupId === UserGroups.ADMIN;
      const isPdr =
      currentUser?.userGroup.userGroupId === UserGroups.PARCEL_DELIVERY_REQUEST;

      if (isWarehouseWorker) {
        const sideBarItems = [
          { id: "warehouseDashboard", name: "dashboard" },
          ...orderStates
            .filter((state) => state.isVisibleForWarehouse)
            .map((state) => ({
              id: state.orderStateId,
              name: state.name,
              countable: true,
            })),
          // {id: 'additionalServices', name: 'additional_services'}
        ];

        dispatch(changeSidebarItems(sideBarItems));
        dispatch(changeState(2));
        dispatch(getOrdersStatesSummary());

        if (!location.pathname.includes(`/${language}/management/`)) {
          navigate(`../${language}/management/warehouse-orders/my`, {
            replace: true,
          });
        }
      } else if (isAdmin) {
        const sideBarItems = [
          { id: "dashboard", name: "dashboard" },
          { id: "customers", name: "customers" },
          { id: "allOrders", name: "all_orders" },
          { id: "warehouseWorkers", name: "warehouse_workers" },
          { id: "warehouses", name: "warehouses" },
          { id: "messages", name: "messages" },
          { id: "staticValues", name: "static_values" },
          { id: "history", name: "history" },
        ];

        dispatch(changeSidebarItems(sideBarItems));

        if (location.pathname.includes(`/${language}/management/customers`)) {
          dispatch(changeState("customers"));
        } else if (
          location.pathname.includes(
            `/${language}/management/warehouse-workers`,
          )
        ) {
          dispatch(changeState("customers"));
        } else if (
          location.pathname.includes(`/${language}/management/dashboard`)
        ) {
          dispatch(changeState("dashboard"));
        }

        if (!location.pathname.includes(`/${language}/management/`)) {
          navigate(`../${language}/management/customers`, { replace: true });
        }
      } else if (isLocalWarehouseWorker) {
        const sideBarItems = [
          // { id: "dashboard", name: "dashboard" },
          ...orderStates
            .filter((state) => state.isVisibleForLocalWarehouse)
            .map((state) => ({
              id: state.orderStateId,
              name: state.name,
              countable: true,
            })),
          // {id: 'additionalServices', name: 'additional_services'}
        ];

        // Find the indexes
        moveAfter(sideBarItems, "in_local_warehouse", "in_customs_warehouse");
        moveAfter(sideBarItems, "received", "ready_for_pickup");

        dispatch(changeSidebarItems(sideBarItems));
        dispatch(changeState(4));
        dispatch(getOrdersStatesSummary());

        if (!location.pathname.includes(`/${language}/management/`)) {
          navigate(`../${language}/management/local-warehouse-orders/my`, {
            replace: true,
          });
        }
      } else if (isPdr) {
        const sideBarItems = [
          { id: "mobileOrders", name: "mobile_orders" },
          { id: "mobileUsers", name: "mobile_users" },
        ];
        dispatch(changeSidebarItems(sideBarItems));

        if (location.pathname.includes(`/${language}/management/user/settings`)) {
          navigate(`../${language}/management/user/settings`, { replace: true });
        } else if (location.pathname.includes(`/${language}/management/pdr/orders`)) {
          navigate(`../${language}/management/pdr/orders`, { replace: true });
          } else if (location.pathname.includes(`/${language}/management/pdr/users`)) {
          navigate(`../${language}/management/pdr/users`, { replace: true });
        } else {
          navigate(`../${language}/management/pdr/orders`, { replace: true });
        }
      } else {
        const sideBarItems = [
          ...orderStates
            .filter((state) => state.isVisibleForCustomer)
            .map((state) => ({
              id: state.orderStateId,
              name: state.name,
              countable: true,
            })),
        ];

        dispatch(changeSidebarItems(sideBarItems));
        dispatch(getOrdersStatesSummary());
      }

      setIsReady(true);
    }
  }, [orderStates, currentUser, location?.pathname]);

  if (isReady && languageId == paramsLanguageId && headerRef) {
    const isAdmin = currentUser?.userGroup.userGroupId === UserGroups.ADMIN;
    const isWarehouseWorker =
      currentUser?.userGroup.userGroupId === UserGroups.WAREHOUSE_WORKER;
    const isLocalWarehouseWorker =
      currentUser?.userGroup.userGroupId === UserGroups.LOCAL_WAREHOUSE_WORKER;
    const isPdr =
      currentUser?.userGroup.userGroupId === UserGroups.PARCEL_DELIVERY_REQUEST;

    return (
      <>
        {isInLanguagePage ? (
          <HomePage />
        ) : (
          <MessageLoader
            key={MessageSections.MAIN + languageId}
            sectionName={MessageSections.MAIN}
          >
            {/*<LiveChat/>*/}
            <div ref={headerRef}>
              <Header
                isAdmin={isAdmin}
                isWarehouseWorker={isWarehouseWorker}
                isLocalWarehouseWorker={isLocalWarehouseWorker}
                hasValidatedRecipient={hasValidatedRecipient}
              />
            </div>

            {isInAuthPage && !matchesPhone ? <AuthBackground /> : ""}

            <Outlet
              context={{
                isAdmin: isAdmin,
                isWarehouseWorker: isWarehouseWorker,
                isLocalWarehouseWorker: isLocalWarehouseWorker,
                hasValidatedRecipient: hasValidatedRecipient,
                isPdr: isPdr
              }}
            />

            <Snackbar
              autoHideDuration={1000}
              open={isLoading}
              children={
                <LoadingSpinnerContainer elevation={3}>
                  <CircularProgress />
                </LoadingSpinnerContainer>
              }
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            />
          </MessageLoader>
        )}
      </>
    );
  } else {
    return <></>;
  }
});
